import type { ProgramTemplate } from '../../types/templates';
import { expandProgramTemplate } from '../../utils/templates';

type GlobalState = {
  programs: Record<string, any>;
  clientPrograms: Array<{ clientId: string; programId: string; status: 'active' | 'paused' | 'completed' }>;
  clientEvents: Array<any>;
  programTemplates: ProgramTemplate[];
};

export function assignProgramTemplateToClient(
  state: GlobalState,
  templateId: string,
  clientId: string,
  startDate: Date,
  programNameOverride?: string,
): GlobalState {
  const tmpl = state.programTemplates.find((t) => t.id === templateId);
  if (!tmpl) return state;

  const programId = `${templateId}_${Date.now()}`;
  const program = expandProgramTemplate(tmpl, programId, startDate, programNameOverride);
  // Debug dump (disabled by default). Enable by setting VITE_DEBUG_PROGRAM_WEEKS=true
  if (import.meta?.env?.VITE_DEBUG_PROGRAM_WEEKS === 'true') {
    try {
      const w = program.totalWeeks;
      console.group('[debug] Expanded program weeks');
      for (let i = 0; i < Math.min(w, 12); i++) {
        const slice = program.days.slice(i * 7, i * 7 + 7).map((d) => ({
          week: i + 1,
          date: d.date,
          dow: new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' }),
          title: d.title,
          type: d.type,
        }));
        console.table(slice);
      }
      console.groupEnd();
    } catch {
      // OBS-11 a-ignore: dev-only debug logging must never break the real code path.
    }
  }

  const newState: GlobalState = {
    ...state,
    programs: { ...state.programs, [programId]: program },
    clientPrograms: [
      // mark other active programs for this client as paused (mock behavior)
      ...state.clientPrograms
        .filter((cp) => cp.clientId !== clientId)
        .concat(
          state.clientPrograms
            .filter((cp) => cp.clientId === clientId && cp.status === 'active')
            .map((cp) => ({ ...cp, status: 'paused' as const })),
        ),
      { clientId, programId, status: 'active' as const },
    ],
    clientEvents: [
      {
        id: `ev_${programId}`,
        clientId,
        type: 'program_updated',
        summary: `Coach assigned program: ${program.name}`,
        detail: `Start: ${program.start} • Duration: ${program.totalWeeks} weeks`,
        severity: 'medium',
        createdAt: new Date().toISOString(),
        viewUrl: `/clients/${clientId}/programs`,
        read: false,
      },
      ...state.clientEvents,
    ],
  };

  return newState;
}
